const POPUP_CLOSED_POLL_MS = 300

type AuthReadyMessage = { type: 'district:auth-ready' }
type ConnectedMessage = { type: 'district:shopify-connected' }
type ConnectFailedMessage = { type: 'district:shopify-connect-failed'; message?: string }

type ConnectPopupCallbacks = {
  onClosed: () => void
  onAuthReady: () => void
  onConnected: () => void
  onFailed: (message: string | undefined) => void
}

/**
 * Polls the connect popup for closure and routes its messages to the callbacks. The returned function stops the poll
 * and removes the listener; the caller must call it on every terminal event.
 */
export function watchConnectPopup(popup: Window, targetOrigin: string, callbacks: ConnectPopupCallbacks): () => void {
  function onMessage(event: MessageEvent) {
    if (event.origin !== targetOrigin || event.source !== popup) {
      return
    }

    if (isAuthReadyMessage(event.data)) {
      callbacks.onAuthReady()
      return
    }

    if (isConnectedMessage(event.data)) {
      callbacks.onConnected()
      return
    }

    if (isConnectFailedMessage(event.data)) {
      callbacks.onFailed(event.data.message)
    }
  }

  const closedPoll = globalThis.setInterval(() => {
    if (popup.closed) {
      callbacks.onClosed()
    }
  }, POPUP_CLOSED_POLL_MS)

  globalThis.addEventListener('message', onMessage)

  return () => {
    globalThis.clearInterval(closedPoll)
    globalThis.removeEventListener('message', onMessage)
  }
}

// Call this only before the popup navigates. After it reaches the auth service origin, the same-origin
// policy blocks writes to its document.
export function showPopupMessage(popup: Window, message: string): void {
  const { body } = popup.document
  if (!body) {
    return
  }
  popup.document.title = 'District'
  body.textContent = message
  body.style.cssText =
    'display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;color:#202223;text-align:center;padding:0 24px'
}

// The Graph client has no request timeout. Without this timeout, an unresolved lookup leaves the
// pre-opened popup blank and the button in a pending state.
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  const { promise: timeout, reject } = Promise.withResolvers<never>()
  const timer = globalThis.setTimeout(() => {
    reject(new Error('Request timed out'))
  }, ms)
  return Promise.race([promise, timeout]).finally(() => {
    globalThis.clearTimeout(timer)
  })
}

function isAuthReadyMessage(data: unknown): data is AuthReadyMessage {
  return !!data && typeof data === 'object' && (data as Partial<AuthReadyMessage>).type === 'district:auth-ready'
}

function isConnectedMessage(data: unknown): data is ConnectedMessage {
  return !!data && typeof data === 'object' && (data as Partial<ConnectedMessage>).type === 'district:shopify-connected'
}

function isConnectFailedMessage(data: unknown): data is ConnectFailedMessage {
  return (
    !!data
    && typeof data === 'object'
    && (data as Partial<ConnectFailedMessage>).type === 'district:shopify-connect-failed'
  )
}
